import React from 'react';
import { Editor } from '@tiptap/core';
import { EditorContent } from '@tiptap/react';
import { Bold, Italic, Sparkles, Underline, Loader2 } from 'lucide-react';

interface SummaryEditorProps {
  editor: Editor | null;
  menuRef: React.RefObject<HTMLDivElement | null>;
  blockOpen: boolean;
  setBlockOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSaving: boolean;
  onSave: () => void;
  onSend: () => void;
  isSendDisabled: boolean;
  isCompleting: boolean;
  hasEnqueuedSummaries: boolean;
  readOnly?: boolean;
}

const inlineActions = [
  { label: 'Sparkles', icon: <Sparkles className="w-4 h-4 text-brand-red" />, isActive: (e: any) => e.isActive('sparkles'), run: (e: any) => {} },
  { label: 'Bold', icon: <Bold className="w-4 h-4" />, isActive: (e: any) => e.isActive('bold'), run: (e: any) => e.chain().focus().toggleBold().run() },
  { label: 'Italic', icon: <Italic className="w-4 h-4" />, isActive: (e: any) => e.isActive('italic'), run: (e: any) => e.chain().focus().toggleItalic().run() },
  { label: 'Underline', icon: <Underline className="w-4 h-4" />, isActive: (e: any) => e.isActive('underline'), run: (e: any) => e.chain().focus().toggleUnderline().run() },
];

const blockActions = [
  { label: 'Paragraaf', isActive: (e: any) => e.isActive('paragraph'), run: (e: any) => e.chain().focus().setParagraph().run() },
  { label: 'H1', isActive: (e: any) => e.isActive('heading', { level: 1 }), run: (e: any) => e.chain().focus().toggleHeading({ level: 1 }).run() },
  { label: 'H2', isActive: (e: any) => e.isActive('heading', { level: 2 }), run: (e: any) => e.chain().focus().toggleHeading({ level: 2 }).run() },
  { label: 'H3', isActive: (e: any) => e.isActive('heading', { level: 3 }), run: (e: any) => e.chain().focus().toggleHeading({ level: 3 }).run() },
  { label: 'Lijst', isActive: (e: any) => e.isActive('bulletList') || e.isActive('listItem'), run: (e: any) => e.chain().focus().toggleBulletList().run() },
];

const SummaryEditor: React.FC<SummaryEditorProps> = ({
  editor,
  menuRef,
  blockOpen,
  setBlockOpen,
  isSaving,
  onSave,
  onSend,
  isSendDisabled,
  isCompleting,
  hasEnqueuedSummaries,
  readOnly = false,
}) => (
  <>
    {/* pt-0: the bubble menu below is hidden with `visibility`, so it is taken
        out of flow with `absolute` — left in flow it reserved its own height
        and opened a large gap above the editor. */}
    <div className="relative px-6 pt-0 pb-6 md:px-8">
      {!readOnly && <div
        ref={menuRef}
        style={{ visibility: 'hidden' }}
        className="absolute z-50 flex items-center gap-1 rounded-xl border border-[#E1D6C6] bg-white/95 p-1 shadow-lg"
      >
        {inlineActions.map(a => (
          <button
            key={a.label}
            onClick={() => a.run(editor)}
            className={[
              'p-2 rounded-lg hover:bg-brand-secondary/50',
              a.isActive(editor) && 'bg-brand-secondary'
            ].filter(Boolean).join(' ')}
          >
            {a.icon}
          </button>
        ))}

        <div className="mx-1 h-5 w-px bg-[#E1D6C6]" />

        <div className="relative">
          <button onClick={() => setBlockOpen(v => !v)} className="px-2 py-1 text-sm rounded-lg hover:bg-brand-secondary/50 text-brand-blauw">
            {editor?.isActive('bulletList') ? 'Lijst'
              : editor?.isActive('heading', { level: 1 }) ? 'H1'
              : editor?.isActive('heading', { level: 2 }) ? 'H2'
              : editor?.isActive('heading', { level: 3 }) ? 'H3'
              : 'Paragraaf'} ▾
          </button>
          {blockOpen && (
            <div
              className="absolute left-0 mt-1 min-w-[12rem] overflow-hidden rounded-lg border border-[#E1D6C6] bg-white shadow-lg"
              onMouseLeave={() => setBlockOpen(false)}
            >
              {blockActions.map(b => (
                <button
                  key={b.label}
                  onClick={() => { b.run(editor); setBlockOpen(false) }}
                  className={[
                    'block w-full px-3 py-2 text-left text-sm hover:bg-brand-secondary/50 text-brand-blauw',
                    b.isActive(editor) && 'bg-brand-secondary font-medium'
                  ].filter(Boolean).join(' ')}
                >
                  {b.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>}

      <div className="rounded-2xl bg-white px-3 py-5 md:px-6 md:py-6 shadow-sm">
        <EditorContent editor={editor} />
      </div>
    </div>

    {/* A sent questionnaire is read-only: neither saving nor re-sending it is
        possible, so the whole action row goes away. */}
    {!readOnly && (
      <div className="px-6 py-4 md:px-8">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-between items-center">
          {/* Both buttons use the shared button classes so they match in size,
              radius and hover behaviour; only the colour distinguishes the
              send action as the primary next step. */}
          <button
            onClick={onSave}
            disabled={isSaving}
            className="btn-gesprekskompas-primary sm:w-auto"
          >
            {isSaving && <Loader2 className="w-5 h-5 animate-spin" />}
            {isSaving ? 'Opslaan...' : 'Bewaar resultaat'}
          </button>

          <button
            onClick={onSend}
            disabled={isSendDisabled}
            title={isSendDisabled ? (hasEnqueuedSummaries ? 'Wacht tot alle samenvattingen klaar zijn' : 'Verzenden is niet beschikbaar') : 'Verstuur de vragenlijst naar CompetenSYS'}
            className="btn-gesprekskompas-accent sm:w-auto"
          >
            {(isCompleting || hasEnqueuedSummaries) && <Loader2 className="w-5 h-5 animate-spin" />}
            {isCompleting
              ? 'Versturen...'
              : hasEnqueuedSummaries
              ? 'Nog bezig met samenvatten'
              : 'Verstuur naar CompetenSYS'}
          </button>
        </div>
      </div>
    )}
  </>
);

export default SummaryEditor;
